/**
 * The ENHANCED overcast deck — a cloud base you can see, fly under and climb
 * out of. **This one is a declared port extension**, and the only cloud in this
 * port that is not the original's own geometry.
 *
 * ## Why it exists, and why it is not on the authentic path
 *
 * `cloud-field.ts`'s `COVER` is `RBMB.EXE`'s own table and it says
 * `overcast: { minCount: 0, maxCount: 0 }`. The Mission Builder places **zero**
 * clouds for an overcast mission, and `sun.c` `0xf1ac` says why: overcast is a
 * *sky repaint*, one flat `BARON.PAL` 84 below the deck and a flat 84 ground
 * above it (`overcast-sky.ts`). Both halves are recovered, both are exact, and
 * the port draws them.
 *
 * The trouble is what that looks like on a 1280 x 800 swapchain rather than on a
 * 320 x 200 VGA screen: **a featureless grey wall**, from the horizon to the
 * zenith, with nothing in it to say it is cloud rather than a blank frame. The
 * user's report — "I barely see any clouds, especially in overcast" — is a
 * correct reading of an authentic picture, and a quarter of generated sorties
 * brief that word (`mission-build.ts` draws it uniformly from four).
 *
 * So the split is the one this port already uses everywhere else: **ORIGINAL
 * keeps the recovered repaint exactly, byte for byte, and ENHANCED hangs a real
 * deck in front of it.** No data moves, `COVER` is untouched, and
 * `?treatment=measured` still reaches the flat `#bababa` reference.
 *
 * ## What is recovered and what is chosen
 *
 * **Recovered:** the altitude. `OVERCAST_DECK_ALTITUDE_M` is
 * `sun.c`'s own `BAND_THRESHOLDS[1]` — the height its `band < 2` test switches
 * on, 9,562.75 ft. Hanging the sheet exactly there means the drawn cloud and the
 * repaint cross at the same instant *by construction*: you break out of the
 * geometry on the same frame the sky hands the blue dome back and repaints the
 * ground as cloud tops. Nothing had to be tuned to make those agree.
 *
 * **Recovered:** the paint. Four entries of `BARON.PAL`'s 64-87 neutral run,
 * walked about the hour's own deck grey (`overcastDeckRamp`).
 *
 * **Chosen:** that the base has structure at all, and the shape of it. A flat
 * sheet at one altitude is still a flat sheet; what makes it read as cloud is
 * four tones of it, cut by three hard steps on a value-noise field of **world**
 * XZ. Hard steps rather than a smooth blend for `sky-clouds.ts`'s reason — a
 * hard terminator is what the 1990 artists would have done with the budget —
 * and world XZ rather than the sheet's own UV so the pattern is anchored to the
 * ground and gives real parallax as you fly under it, instead of being glued to
 * the camera.
 *
 * ## G23: the deck used to read as a ceiling, and two changes that are one idea
 *
 * The first version had three tones and **all of them were at or below the
 * sky's own colour**, so the only thing the field could do was darken. That is
 * precisely the defect the slab lane had already measured next door and written
 * into `sky-clouds.ts`: a base that can only darken reads as flat, because the
 * face a pilot sees most of the time is *already* the ramp's bottom entry — an
 * ENHANCED `cloudy` deck seen from below measured 148,639 px of a single
 * `#929292` against ORIGINAL's 153,935 of the same. Its conclusion is quoted on
 * `OvercastDeckRamp.bright`, and taking it costs one more `step`.
 *
 * The second is a range fade, and it is not a separate idea — it is what makes
 * the first one safe. This file used to protect the seam where the sheet runs
 * out toward the horizon by *forbidding the deck to be brighter than the sky
 * anywhere*, which bought a seamless join at the cost of the whole upper half of
 * the ramp. Fading the sheet to `ramp.lit` with slant range protects the same
 * seam **where the seam actually is** — the sheet has already flattened to the
 * dome's own entry long before it reaches it — and hands the near field its
 * bright tone back. See `STRUCTURE_FULL_M`.
 *
 * ## Measured, including the part that did not move
 *
 * Swapchain census of an overcast noon over Verdun, cockpit view, greys only
 * (the deck lives on `BARON.PAL`'s neutral run, so anything with a channel
 * spread is a strut or a wire and is excluded). Three horizontal strips, near
 * the top of the screen looking steeply up through to just above the skyline:
 *
 * | strip | before | after |
 * | --- | --- | --- |
 * | near, ~4.3 km slant | sd **14.73**, three tones, nothing above `#bababa` | sd **17.24**, bright tone at **5.1 %** |
 * | mid | sd **18.03**, `#8a8a8a`-`#bababa`, a 48-unit spread | sd **8.64**, bunched on the dome's own grey |
 * | far, at the skyline | **90.6 %** flat `#bababa` | **90.5 %** |
 *
 * The near strip gains structure and the mid strip loses it, which is the whole
 * intent stated as two numbers. **The far strip is the interesting one and it
 * is a null result**: it was already flat before this change, because past the
 * far plane the sheet is clipped and what fills the skyline is the repainted
 * *dome*, not the deck. So the fade is not fixing a visible horizon problem —
 * there was not one — it is a **guarantee**, and the right way to read the
 * unchanged 90.5 % is that the guarantee holds. Do not restate this as "the
 * fade cleaned up the horizon"; it did not, and the mid-strip halving is the
 * only place it shows.
 *
 * One thing this lane did **not** measure: the claim in `STRUCTURE_FULL_M`
 * that the far field shimmered under motion. It is a sound reading of a 190 m
 * octave at 20 km, and it is still a reading rather than a measurement — a
 * still-frame census cannot see it.
 *
 * Both changes are behind the ENHANCED SKY toggle, where taste is sanctioned;
 * ORIGINAL and `?treatment=measured` are untouched and still reach the flat
 * `#bababa`.
 *
 * ## Construction notes
 *
 * **One quad, one draw call, no texture, no transparency.** The sheet is opaque
 * and depth-writing, which is the rule `three/screen-sprites.ts` states and
 * which this renderer's reversed-Z has now enforced five times over
 * (`lessons.md`): `transparent: true` and `depthWrite: false` each rasterise
 * zero pixels here. Being opaque also means the deck occludes the sun, the
 * stars and the moon for free, which is exactly what §12 of
 * `enhanced-sky.md` had to arrange by hand for the flat dome.
 *
 * **It rides the camera in XZ**, like `world.ts`'s dome and for the same reason:
 * a finite sheet centred on the mission origin would run out, and a cloud deck
 * you can fly off the edge of is worse than no deck. The half-extent is one
 * `CAMERA_FAR_M` plus margin, so the sheet always reaches the far plane in every
 * bearing and never shows an edge.
 *
 * **It is one sheet, not two.** A slab with thickness would enclose the pilot in
 * grey for as long as it took to climb through it — a minute of blindness at a
 * scout's climb rate — and the pop of a razor-thin sheet crossing the eye lasts
 * one frame. `sky-clouds.ts` records the same trade for the 1990 slabs, whose
 * 1,000-foot thickness is the reason they had to grow a cull.
 */

import * as THREE from "three/webgpu";
import type { OvercastDeckRamp } from "./overcast-sky.ts";
import { OVERCAST_DECK_ALTITUDE_M } from "./overcast-sky.ts";
import type { PaletteColour } from "./time-of-day.ts";
import { CAMERA_FAR_M } from "./flight-view.ts";
import { quantiseToVgaDac } from "../three/ordered-dither.ts";
import { cloudFieldAtFragment } from "../three/cloud-noise.ts";

/**
 * Half-width of the sheet, in metres. A fragment of it can be at most
 * `CAMERA_FAR_M` from the eye and still be drawn, so anything past that is
 * margin against a camera that has been rolled or is looking along the sheet.
 */
export const DECK_HALF_EXTENT_M = CAMERA_FAR_M * 1.2;

/**
 * Where the three hard steps fall on the noise field, and so how much of the
 * deck is each tone. 0.44 and 0.62 about a field whose mean is 0.5 put roughly
 * a third of the sheet in each of the lower tones, with the darkest a little
 * the largest — a cloud base is mostly its bellies.
 *
 * `BRIGHT_STEP` is close behind `LIT_STEP` and that is not a typo — it is
 * where the field's own distribution put it. The field is a weighted sum of
 * four octaves, so it is bell-shaped about its mean rather than uniform and its
 * tail is far steeper than the even spacing of the lower two steps suggests.
 * Measured on the swapchain over 33,000 deck pixels: **9.4 % of the sheet is
 * above 0.62 and only 1.6 % is above 0.70**. Choosing the threshold by eye from
 * the lower steps' spacing gave a bright tone that was effectively never drawn;
 * 0.65 was read off that census and lands the bright patches at about a
 * twentieth of the sheet.
 *
 * A twentieth, and not a fourth equal share: a cloud base that is a quarter
 * brighter than the sky is a *broken* overcast, which is a different word in
 * the weather table.
 */
const MID_STEP = 0.44;
const LIT_STEP = 0.62;
const BRIGHT_STEP = 0.65;

/**
 * How far the deck's structure survives, in metres of slant range (G23).
 *
 * The sheet reached the far plane at full contrast, which is wrong twice over.
 * It is wrong physically — 26 km of air between the eye and a cloud base washes
 * it out, and the deck was the only surface in this port with no aerial
 * perspective at all. And it is wrong at the pixel: `cloud-noise.ts`'s finest
 * octave is 190 m, which at 20 km subtends about a hundredth of a degree, so the
 * far half of the sheet was carrying detail finer than the framebuffer could
 * hold and shimmering as the aeroplane moved.
 *
 * Converging on `ramp.lit` rather than on grey-in-general is the load-bearing
 * part, and it is what *licences* the `bright` tone above. `lit` is the entry
 * `overcastRepaintFor` paints the whole dome (`overcast-sky.ts`), so a sheet
 * that has flattened to `lit` before it reaches the horizon meets the dome in
 * exactly the dome's own colour — the seam this file's header cared about stays
 * invisible, and it now stays invisible *whatever* the near field is doing.
 * Before, the seam was protected by forbidding the deck to be brighter than the
 * sky anywhere; now it is protected where the seam actually is.
 *
 * The numbers: full strength within twice the deck's own height, which is the
 * cone inside about 30 degrees of overhead where a pilot reads the base as
 * texture; gone by 20 km, comfortably inside `CAMERA_FAR_M` so the sheet is
 * already flat well before its own edge.
 */
const STRUCTURE_FULL_M = OVERCAST_DECK_ALTITUDE_M * 2;
const STRUCTURE_GONE_M = 20000;

type Vec3Node = THREE.Node<"vec3">;

function paletteVec3(colour: PaletteColour): Vec3Node {
  return THREE.TSL.vec3(((colour >> 16) & 0xff) / 255, ((colour >> 8) & 0xff) / 255, (colour & 0xff) / 255);
}

/**
 * Three hard steps across `overcastDeckRamp`'s walk, faded to the sky's own
 * entry with range. Exported so `overcast-deck.test.ts` can pin the material's
 * depth flags without a DOM, the arrangement `sky-clouds.ts` and
 * `screen-sprites.ts` both use.
 *
 * Hard `step`s and not a blend, still: three tones and a hard terminator is what
 * the 1990 artists would have done with the budget, and every tone is a real
 * `BARON.PAL` entry of the 64-87 run rather than a multiplication of one.
 */
export function overcastDeckMaterial(ramp: OvercastDeckRamp): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide, fog: false });
  const field = cloudFieldAtFragment();
  const shaded = THREE.TSL.mix(paletteVec3(ramp.shadow), paletteVec3(ramp.mid), THREE.TSL.step(MID_STEP, field));
  const lit = THREE.TSL.mix(shaded, paletteVec3(ramp.lit), THREE.TSL.step(LIT_STEP, field));
  const structured = THREE.TSL.mix(lit, paletteVec3(ramp.bright), THREE.TSL.step(BRIGHT_STEP, field));
  // Euclidean range from the eye, not a view-space depth — the same call
  // `searchlight-beams.ts` makes and for the same reason: a range term that uses
  // depth changes its answer when the pilot turns his head, and a cloud base
  // must not repaint itself because you glanced at it.
  const range = THREE.TSL.positionView.length();
  const washedOut = THREE.TSL.smoothstep(STRUCTURE_FULL_M, STRUCTURE_GONE_M, range);
  material.colorNode = quantiseToVgaDac(THREE.TSL.mix(structured, paletteVec3(ramp.lit), washedOut));
  return material;
}

/**
 * The sheet. Add it to the sky dressing's backdrop; it needs no per-frame call
 * from the scene because it recentres itself in `onBeforeRender`, the hook
 * `world.ts`'s dome already uses.
 *
 * `frustumCulled` is off for that reason and not for cost: the frustum test runs
 * *before* `onBeforeRender`, so it would read a bounding sphere from wherever
 * the sheet was last frame and drop it the moment the camera turned.
 */
export function createOvercastDeck(ramp: OvercastDeckRamp): THREE.Mesh {
  const extent = DECK_HALF_EXTENT_M * 2;
  const sheet = new THREE.Mesh(new THREE.PlaneGeometry(extent, extent), overcastDeckMaterial(ramp));
  sheet.rotation.x = -Math.PI / 2;
  sheet.position.y = OVERCAST_DECK_ALTITUDE_M;
  sheet.frustumCulled = false;
  sheet.onBeforeRender = (_renderer, _scene, camera) => {
    camera.getWorldPosition(EYE);
    sheet.position.set(EYE.x, OVERCAST_DECK_ALTITUDE_M, EYE.z);
    sheet.updateMatrixWorld();
  };
  return sheet;
}

const EYE = new THREE.Vector3();

/** Re-exported so a caller placing the deck does not also have to import `overcast-sky.ts` for its height. */
export { OVERCAST_DECK_ALTITUDE_M };
